/* import __COLOCATED_TEMPLATE__ from './settings.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type FinVoice from 'embercom/models/operator/visual-builder/step/fin-voice';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import { State } from 'embercom/models/data/matching-system/matching-constants';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type { FinVoiceVoiceLanguage } from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import type {
  FinVoiceRunbook,
  WorkflowSelection,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import type ConversationAttributeDescriptor from 'embercom/objects/inbox/conversation-attribute-descriptor';

interface Arguments {
  step: FinVoice;
  isReadOnly: boolean;
  editorState: EditorState;
  numberConfiguration?: FinVoiceNumberConfiguration;
  classificationAttributeDescriptors: ConversationAttributeDescriptor[];
}

export default class Settings extends Component<Arguments> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare contentEditorService: any;
  @service declare appService: AppService;
  @service declare attributeService: any;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare notificationsService: $TSFixMe;

  @tracked openSectionId = '';
  @tracked setupAccordionOpenSectionId = '';

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);
  }

  get currentWorkflowSelection(): WorkflowSelection {
    let config = this.numberConfiguration;
    if (!config) {
      return { type: 'runbook', runbook: 'none' };
    }

    // Check if a procedure is saved
    if (config.handoverConfiguration?.procedureId) {
      return {
        type: 'procedure',
        procedureId: config.handoverConfiguration.procedureId,
      };
    }

    // Default to runbook
    return {
      type: 'runbook',
      runbook: config.runbook || 'none',
    };
  }

  @action
  openAndScrollToAccordionSection(sectionId: string) {
    // Set the accordion section to open
    this.openSectionId = sectionId;

    // Scroll to the accordion section after allowing it to open
    requestAnimationFrame(() => {
      let targetElement = document.querySelector(`[data-accordion-section="${sectionId}"]`);
      targetElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    });
  }

  get liveGuidelines() {
    return this.store
      .peekAll('ai-agent/guidelines')
      .rejectBy('isDeleted')
      .filterBy('state', State.Live)
      .toArray();
  }

  get liveGuidelinesCount() {
    return this.liveGuidelines.length;
  }

  get categoriesCount() {
    return this.attributeService.aiCategoriesDescriptors.filterBy('isEnabled', true).length;
  }

  get escalationGuidanceCount() {
    return this.liveGuidelines.filter((guideline) => guideline.category === 'routing').length;
  }

  get escalationRulesCount() {
    return this.store.peekAll('ai-agent/escalation-rule').filterBy('enabled', true).length;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get classificationAttributeDescriptors() {
    return this.args.classificationAttributeDescriptors;
  }

  get canUseFinVoiceRunbooksOrProcedures() {
    return this.appService.app.canUseFinVoiceRunbooksOrProcedures;
  }

  get canUseGoodbyeMessage() {
    return this.appService.app.canUseFeature('fin-voice-goodbye-message');
  }

  @action
  async updateFinBehavior(behavior: {
    voice: FinVoiceVoice;
    greeting: string;
    language: FinVoiceVoiceLanguage;
  }) {
    let config = this.numberConfiguration;
    if (!config) {
      return;
    }

    try {
      config.voice = behavior.voice;
      config.greeting = behavior.greeting;

      config.ivrLanguage = behavior.language;

      await config.save();
      this.args.step.finVoiceNumberConfigurationId = config.id;
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async updateFinIssueCategorization(attributes?: string[]) {
    let config = this.numberConfiguration;
    if (!config) {
      return;
    }

    try {
      config.classificationAttributes = attributes;
      await config.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async updateAskForCsatRating(
    askForCsatRating: boolean,
    csatMessage: string,
    csatTtsLanguage: string,
  ) {
    let config = this.numberConfiguration;
    if (!config) {
      return;
    }

    try {
      config.handoverConfiguration.askForCsatRating = askForCsatRating;
      config.handoverConfiguration.csatTtsLanguage = csatTtsLanguage;
      config.csatMessage = csatMessage;
      await config.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async updateRunbook(runbook: FinVoiceRunbook, procedureId: string) {
    let config = this.numberConfiguration;
    if (!config) {
      return;
    }

    try {
      config.runbook = runbook;
      if (procedureId) {
        config.handoverConfiguration.procedureId = procedureId;
      } else {
        config.handoverConfiguration.procedureId = undefined;
      }
      await config.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async updateWorkflow(selection: WorkflowSelection) {
    let config = this.numberConfiguration;
    if (!config) {
      return;
    }

    try {
      if (selection.type === 'procedure') {
        config.handoverConfiguration.procedureId = selection.procedureId;
        config.runbook = 'none';
      } else {
        config.runbook = selection.runbook || 'none';
        config.handoverConfiguration.procedureId = undefined;
      }
      await config.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async saveHandoverConfig(goodbyeMessage: string) {
    let config = this.numberConfiguration;
    if (!config) {
      return;
    }

    try {
      config.ivrGoodbyeMessage = goodbyeMessage;
      await config.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error as Error);
    }
  }

  @action
  async updateAfterPhoneCallEnds(autoCloseWhenNotEscalated: boolean, defaultTeamId?: string) {
    let config = this.numberConfiguration;
    if (!config) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateAfterPhoneCallEnds(
        config,
        autoCloseWhenNotEscalated,
        defaultTeamId,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  private notifyFailure(error: Error) {
    this.notificationsService.notifyResponseError(error, {
      default: this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.failure'),
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::FinVoice::Settings': typeof Settings;
    'workflows/graph-editor/node-items/steps/fin-voice/settings': typeof Settings;
  }
}
